import React from 'react';
import { Document, Font, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import WorkSansBold from 'assets/fonts/WorkSans-Bold.ttf';
import WorkSansItalic from 'assets/fonts/WorkSans-Italic.ttf';
import WorkSansMedium from 'assets/fonts/WorkSans-Medium.ttf';
import WorkSansRegular from 'assets/fonts/WorkSans-Regular.ttf';
import LogoIcon from 'assets/icons/eiq-logo-r.png';
import { StrToDateTimeLocal } from 'common/helpers/date.helper';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import { CitationStatus } from 'features/eiq-manage/enums/citation-status.enum';
import { getRegisteredTo, getStaticMapUrl, isGuestCitation } from '../helpers/citation-utils';
import { getCitationCountText } from '../helpers/citations-helper';
import { IPrintCitaitonDetailsData, IPrintCitationDetailsProps } from './print-citation-details.types';

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
const dateTimeFormat = 'MM/DD/YYYY h:mmA';

Font.registerHyphenationCallback((word) => [word]);
Font.register({
  family: 'Work Sans',
  fonts: [
    { src: WorkSansRegular, fontWeight: 400, fontStyle: 'normal' },
    { src: WorkSansMedium, fontWeight: 500, fontStyle: 'normal' },
    { src: WorkSansBold, fontWeight: 600, fontStyle: 'normal' },
    { src: WorkSansItalic, fontWeight: 400, fontStyle: 'italic' },
  ],
});

interface IReportHeaderProps {
  logo?: string | null;
  communityName: string;
  data: IPrintCitaitonDetailsData;
  formattedOccurredAt?: string;
  isAutoIssueInvoice: boolean;
}

const ReportHeader = ({ logo, communityName, data, formattedOccurredAt, isAutoIssueInvoice }: IReportHeaderProps) => (
  <View style={styles.header}>
    <View>
      {logo ? <Image src={logo} style={styles.logo} /> : <Text>{communityName}</Text>}
      <Text style={[styles.bold, styles.text]}>
        Citation# {data.externalId} | {formattedOccurredAt}
      </Text>
    </View>

    {data.paymentLinkQRSrc && isAutoIssueInvoice && (
      <View style={styles.qrWrapper}>
        <Image src={data.paymentLinkQRSrc} style={styles.qr} />
        <Text style={[styles.italic, styles.text]}>Scan for payment link</Text>
      </View>
    )}
  </View>
);

const ReportFooter = () => (
  <View style={styles.footer} fixed>
    <Image src={LogoIcon} />
  </View>
);

interface IMainNoticeProps {
  citation: ICitationRead;
  isGuest: boolean;
}

const MainNotice = ({ citation, isGuest }: IMainNoticeProps) => {
  const licensePlate = <Text style={styles.bold}>{citation.licensePlate}</Text>;

  if (citation.status === CitationStatus.Warning) {
    return isGuest ? (
      <Text>
        A visitor associated with your property was recorded speeding in the community using vehicle tag {licensePlate}. This is a formal
        warning, and further violations will result in additional penalties.
      </Text>
    ) : (
      <Text>
        We are writing to notify you that your vehicle with tag {licensePlate} was recorded speeding within the community. This is a formal
        warning, and further violations will result in additional penalties.
      </Text>
    );
  }

  return isGuest ? (
    <Text>A visitor associated with your property was recorded speeding in the community using vehicle tag {licensePlate}.</Text>
  ) : (
    <Text>Your vehicle with tag {licensePlate} was recorded speeding within the community.</Text>
  );
};

interface PayNowLinkProps {
  isAutoIssueInvoice?: boolean | null;
  paymentLinkUrl?: string | null;
}

const PayNowLink = ({ isAutoIssueInvoice, paymentLinkUrl }: PayNowLinkProps) => {
  if (!isAutoIssueInvoice || !paymentLinkUrl) {
    return null;
  }

  return (
    <Link src={paymentLinkUrl} style={styles.link}>
      (Pay Now)
    </Link>
  );
};

interface IPenaltyTextProps {
  data: IPrintCitaitonDetailsData;
  isAutoIssueInvoice?: boolean | null;
}

const PenaltyText = ({ data, isAutoIssueInvoice }: IPenaltyTextProps) => {
  if (data.status === CitationStatus.Warning) {
    return (
      <Text>
        This is a <Text style={styles.italic}>warning</Text>. A second citation will result in a fine.
      </Text>
    );
  }

  if (data.totalPaymentAmount && data.suspensionDays) {
    return (
      <Text>
        ${data.totalPaymentAmount} fine with {data.suspensionDays} days transponder suspension
      </Text>
    );
  }

  if (data.totalPaymentAmount) {
    return <Text>${data.totalPaymentAmount} fine</Text>;
  }

  return data.suspensionDays ? <Text>{data.suspensionDays} days transponder suspension</Text> : <Text>N/A</Text>;
};

const preserveSpaces = (line: string) =>
  line
    .replace(/\t/g, '    ')
    .replace(/^ +/, (spaces) => '\u00A0'.repeat(spaces.length))
    .replace(/ {2,}/g, (spaces) => '\u00A0'.repeat(spaces.length));

const PreservedText = ({ text }: { text?: string | null }) => (
  <View>
    {(text ?? '')
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((line, index) => (
        <Text key={`${index}-${line}`}>{line ? preserveSpaces(line) : '\u00A0'}</Text>
      ))}
  </View>
);

type Accent = 'green' | 'orange';

interface IDetailRowProps {
  label: string;
  value: React.ReactNode;
  accent?: Accent;
}

const DetailRow = ({ label, value, accent }: IDetailRowProps) => {
  const valueStyle = accent ? [styles.bold, styles[accent]] : styles.bold;

  return (
    <Text style={styles.text}>
      {label}: <Text style={valueStyle}>{value}</Text>
    </Text>
  );
};

interface IIncidentDetailsProps {
  data: IPrintCitaitonDetailsData;
  formattedOccurredAt?: string;
}

const IncidentDetails = ({ data, formattedOccurredAt }: IIncidentDetailsProps) => (
  <View style={styles.detailsColumn}>
    <Text style={[styles.bold, styles.title]}>INCIDENT DETAILS</Text>
    <DetailRow label="License Plate" value={data.licensePlate} />
    <DetailRow label="Visitor" value={getRegisteredTo(data)} />
    <DetailRow label="Address" value={data.address} />
    <DetailRow label="Speed Limit" value={`${data.speedLimit} mph`} accent="green" />
    <DetailRow label="Recorded Speed" value={`${data.violatingSpeed} mph`} accent="orange" />
    <DetailRow label="Date & Time" value={formattedOccurredAt} />
  </View>
);

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <View style={styles.sectionHeader}>
    <Text style={[styles.title, styles.bold]}>{children}</Text>
  </View>
);

interface IEvidencePanelProps {
  title: string;
  imageSrc?: string | null;
  withDivider?: boolean;
}

const EvidencePanel = ({ title, imageSrc, withDivider = false }: IEvidencePanelProps) => (
  <View style={withDivider ? [styles.evidenceColumn, styles.evidenceColumnDivider] : styles.evidenceColumn}>
    <SectionHeader>{title}</SectionHeader>
    <View style={styles.evidenceImageFrame}>{imageSrc && <Image style={styles.containedImage} src={imageSrc} />}</View>
  </View>
);

interface IEvidenceSectionProps {
  images: string[];
  staticMapUrl?: string | null;
}

const EvidenceSection = ({ images, staticMapUrl }: IEvidenceSectionProps) => {
  const lastPhoto = images[images.length - 1];

  return (
    <View style={styles.evidenceWrapper}>
      <SectionHeader>CAPTURED EVIDENCE</SectionHeader>

      <View style={styles.thumbnailRow}>
        {images.slice(0, 5).map((imageSrc, index) => (
          <View key={`${imageSrc}-${index}`} style={styles.thumbnail} id={`evidence-${index}-image`}>
            <Image style={styles.containedImage} src={imageSrc} />
          </View>
        ))}
      </View>

      <View style={styles.evidenceBody}>
        <EvidencePanel title="Location of the Citation" imageSrc={staticMapUrl} withDivider />
        <EvidencePanel title="Close-up of the License Plate" imageSrc={lastPhoto} />
      </View>
    </View>
  );
};

interface IClosingMessageProps {
  citationGeneralInfo: string | null | undefined;
}

const ClosingMessage = ({ citationGeneralInfo }: IClosingMessageProps) => (
  <View style={[styles.text, styles.closingMessage]}>
    {citationGeneralInfo && (
      <View style={styles.text}>
        <PreservedText text={citationGeneralInfo} />
      </View>
    )}
  </View>
);

export const PrintCitationDetails = ({
  communityLogo,
  data,
  communityName,
  isAutoIssueInvoice,
  citationGeneralInfo,
  legalNotes,
}: IPrintCitationDetailsProps) => {
  const isGuest = isGuestCitation(data);
  const formattedOccurredAt = StrToDateTimeLocal(data.occurredAt)?.format(dateTimeFormat);
  const staticMapUrl = getStaticMapUrl(data.latitude, data.longitude, googleMapsApiKey);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ReportHeader
          logo={communityLogo}
          communityName={communityName}
          data={data}
          formattedOccurredAt={formattedOccurredAt}
          isAutoIssueInvoice={isAutoIssueInvoice}
        />

        <View style={styles.infoBlock}>
          <View style={[styles.noticeColumn, styles.text]}>
            <MainNotice citation={data} isGuest={isGuest} />
            <View>
              <Text style={styles.bold}>
                Citation Count:{' '}
                <Text style={styles.orange}>
                  {getCitationCountText(data.sequenceNumber)} Citation{isGuest ? ' (Visitor)' : ''}
                </Text>
              </Text>
              <Text>
                <Text style={styles.bold}>Penalty: </Text>
                <Text style={styles.normal}>
                  <PenaltyText data={data} isAutoIssueInvoice={isAutoIssueInvoice} />
                </Text>
              </Text>
              <Text>
                <PayNowLink isAutoIssueInvoice={isAutoIssueInvoice} paymentLinkUrl={data.paymentLinkUrl} />
              </Text>
            </View>
          </View>

          <IncidentDetails data={data} formattedOccurredAt={formattedOccurredAt} />
        </View>

        {legalNotes && (
          <View style={styles.text}>
            <PreservedText text={legalNotes} />
          </View>
        )}

        <EvidenceSection images={data.evidenceImagesSrc} staticMapUrl={staticMapUrl} />
        <ClosingMessage citationGeneralInfo={citationGeneralInfo} />
        <ReportFooter />
      </Page>
    </Document>
  );
};

const border = '1pt solid #000000';

const styles = StyleSheet.create({
  page: {
    paddingTop: 15,
    paddingHorizontal: 20,
    paddingBottom: 60,
    fontFamily: 'Work Sans',
    color: '#292B2E',
  },
  text: {
    color: '#292B2E',
    fontSize: 12,
  },
  title: {
    color: '#292B2E',
    fontSize: 14,
  },
  bold: {
    fontWeight: 500,
  },
  normal: {
    fontWeight: 400,
  },
  italic: {
    fontStyle: 'italic',
  },
  orange: {
    color: '#EA793E',
  },
  green: {
    color: '#51A832',
  },
  link: {
    color: '#1677ff',
    textDecoration: 'underline',
    fontSize: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottom: border,
    paddingBottom: 8,
  },
  logo: {
    width: 120,
    marginTop: 5,
    marginBottom: 20,
  },
  qrWrapper: {
    justifyContent: 'space-between',
  },
  qr: {
    width: 70,
    marginLeft: 'auto',
  },
  infoBlock: {
    flexDirection: 'row',
    marginVertical: 10,
    borderBottom: border,
    paddingBottom: 10,
  },
  noticeColumn: {
    width: '60%',
    gap: 4,
    justifyContent: 'space-between',
  },
  detailsColumn: {
    width: '40%',
    paddingLeft: 6,
  },
  sectionHeader: {
    alignItems: 'center',
    backgroundColor: '#E8E8E8',
    borderBottom: border,
  },
  evidenceWrapper: {
    marginTop: 16,
    border,
  },
  thumbnailRow: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 6,
    height: 70,
  },
  thumbnail: {
    flex: 1,
  },
  containedImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'center',
  },
  evidenceBody: {
    flexDirection: 'row',
  },
  evidenceColumn: {
    width: '50%',
    borderTop: border,
  },
  evidenceColumnDivider: {
    borderRight: border,
  },
  evidenceImageFrame: {
    height: 100,
    padding: 5,
  },
  closingMessage: {
    marginTop: 10,
  },
  spacedParagraph: {
    marginTop: 10,
  },
  signature: {
    marginTop: 10,
  },
  signatureCommunity: {
    marginTop: 2,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    position: 'absolute',
    bottom: 10,
    left: 20,
    right: 20,
    height: 20,
    fontSize: 10,
  },
});
